import { useCallback } from 'react';
import { useUIStore } from '../../../../store/useUIStore';
import { useModelStore } from '../../../../store/useModelStore';

/**
 * Enhanced Beam Operations Hook
 * Handles right-click and double-tap interactions for beams
 * Provides context menu with "Truss on Beam" and "Edit Beam Properties" options
 */
export const useEnhancedBeamOperations = () => {
  const uiStore = useUIStore();
  const modelStore = useModelStore();

  // Handle enhanced beam right click (or double-tap on touchpad)
  const handleEnhancedBeamRightClick = useCallback((beamId: string, event?: any) => {
    //console.log('🚀 Enhanced beam right-clicked:', beamId);

    // Prevent default browser context menu
    if (event) {
      event.stopPropagation();
      if (event.preventDefault) {
        event.preventDefault();
      }
      if (event.nativeEvent && typeof event.nativeEvent.preventDefault === 'function') {
        event.nativeEvent.preventDefault();
      }
    }

    // Find the beam to validate it exists
    const beam = modelStore?.beams?.find((b: any) => b.id === beamId);
    if (!beam) {
      console.warn('Beam not found:', beamId);
      return;
    }

    // Calculate screen position for context menu
    let x = 0, y = 0;
    try {
      const canvas = document.querySelector('canvas');
      if (canvas && event?.clientX != null && event?.clientY != null) {
        x = event.clientX;
        y = event.clientY;
      } else if (event?.nativeEvent?.clientX) {
        x = event.nativeEvent.clientX;
        y = event.nativeEvent.clientY;
      } else if (canvas) {
        // Fallback to center of canvas
        const rect = canvas.getBoundingClientRect();
        x = rect.left + rect.width / 2;
        y = rect.top + rect.height / 2;
      }
    } catch (e) {
      console.warn('Error calculating context menu position:', e);
      // Fallback position
      x = 200;
      y = 200;
    }

    //console.log('🎯 Opening enhanced beam context menu at:', { x, y });
    
    // Open the enhanced beam context menu
    uiStore.openEnhancedBeamContextMenu(beamId, { x, y });
  }, [modelStore, uiStore]);

  // Handle beam double click (alternative to right-click)
  const handleEnhancedBeamDoubleClick = useCallback((beamId: string, event?: any) => {
    //console.log('🚀 Enhanced beam double-clicked:', beamId);
    
    if (event) {
      event.stopPropagation();
    }
    
    // Treat double-click same as right-click for touchpad users
    handleEnhancedBeamRightClick(beamId, event);
  }, [handleEnhancedBeamRightClick]);

  // Handle "Truss on Beam" action - opens existing beam truss config panel
  const handleTrussOnBeam = useCallback((beamId: string) => {
    //console.log('🔧 Opening beam truss config panel for beam:', beamId);
    
    // Calculate position for beam truss config panel (offset from context menu)
    const contextMenuPosition = uiStore.enhancedBeamContextMenuPosition;
    const panelPosition = {
      x: contextMenuPosition.x + 50, // Offset to the right
      y: contextMenuPosition.y - 50  // Offset up slightly
    };

    // Open the existing beam truss configuration panel
    uiStore.openBeamTrussConfigPanel(beamId, panelPosition);
  }, [uiStore]);

  // Handle "Edit Beam Properties" action - opens beam properties dialog
  const handleEditBeamProperties = useCallback((beamId: string) => {
    //console.log('📐 Opening beam properties dialog for beam:', beamId);
    
    // Calculate position for beam properties dialog (offset from context menu)
    const contextMenuPosition = uiStore.enhancedBeamContextMenuPosition;
    const dialogPosition = {
      x: contextMenuPosition.x + 50, // Offset to the right
      y: contextMenuPosition.y - 50  // Offset up slightly
    };
    
    // Open the beam properties dialog
    uiStore.openBeamPropertiesDialog(beamId, dialogPosition);
  }, [uiStore]);

  // Handle "Configure BMD" action - opens BMD input panel
  const handleConfigureBMD = useCallback((beamId: string) => {
    console.log('📊 Opening BMD input panel for beam:', beamId);
    
    // Calculate position for BMD input panel (offset from context menu)
    const contextMenuPosition = uiStore.enhancedBeamContextMenuPosition;
    const panelPosition = {
      x: contextMenuPosition.x + 50, // Offset to the right
      y: contextMenuPosition.y - 50  // Offset up slightly
    };
    
    // Open the BMD input panel
    uiStore.openBMDInputPanel(beamId, panelPosition);
  }, [uiStore]);

  // Handle "Section" action - opens section configuration dialog
  const handleSection = useCallback((beamId: string) => {
    console.log('🏗️ Opening section dialog for beam:', beamId);
    
    // Calculate position for section dialog (offset from context menu)
    const contextMenuPosition = uiStore.enhancedBeamContextMenuPosition;
    const dialogPosition = {
      x: contextMenuPosition.x + 50, // Offset to the right
      y: contextMenuPosition.y - 50  // Offset up slightly
    };
    
    // Open the section dialog with selected beam
    uiStore.openBeamSectionDialog([beamId], dialogPosition);
  }, [uiStore]);

  // Close all enhanced beam menus and dialogs
  const closeAllEnhancedBeamInteractions = useCallback(() => {
    uiStore.closeEnhancedBeamContextMenu();
    uiStore.closeBeamTrussConfigPanel();
    uiStore.closeBeamPropertiesDialog();
    uiStore.closeBMDInputPanel();
    uiStore.closeBeamSectionDialog();
  }, [uiStore]);

  return {
    handleEnhancedBeamRightClick,
    handleEnhancedBeamDoubleClick,
    handleTrussOnBeam,
    handleEditBeamProperties,
    handleConfigureBMD,
    handleSection,
    closeAllEnhancedBeamInteractions
  };
};
